/**
 * The live-follow decision for the transcript viewer's scroll handler.
 *
 * This is a pure function because the inline version produced two distinct
 * defects in a single day (2026-09-22), both invisible to touch QA and both
 * hitting keyboard and screen-reader users only:
 *
 *  - a layout guard keyed on `scrollHeight` swallowed upward scrolls, because
 *    `scrollHeight` also grows with every arriving transcript segment;
 *  - a "growth" short-circuit added to fix that suppressed the scroll-to-end
 *    re-arm MU asked for on 17. 9. 2026.
 *
 * The rule the handler needs is narrow: decide nothing when `scrollTop` was
 * moved by the BROWSER, and decide normally when it was moved by the READER.
 * Only two geometry changes can force `scrollTop` down — the viewport growing
 * taller, or the content growing shorter — because the maximum scroll offset
 * is `scrollHeight - clientHeight`. Content being appended raises that maximum
 * and can never lower `scrollTop`, so it must not suppress anything.
 */
/** How close to the live edge counts as "at the end". */
export const FOLLOW_BOTTOM_TOLERANCE_PX = 48;
/**
 * How long after a layout change an upward scroll is treated as the browser
 * re-clamping `scrollTop` rather than the reader scrolling back.
 */
export const FOLLOW_LAYOUT_SETTLE_MS = 400;
/** Upward-scroll deadzone for fractional `scrollTop` jitter. */
export const FOLLOW_SCROLL_UP_EPSILON_PX = 2;
/**
 * Downward-scroll deadzone for the same jitter. Smaller than the upward one,
 * so the short tail of a PageDown or End still counts as moving down, but a
 * fractional sample (495.5 -> 496) straight after a release cannot re-arm.
 */
export const FOLLOW_SCROLL_DOWN_EPSILON_PX = 0.5;
export function decideFollowOnScroll(s) {
    // Only these two can lower `scrollTop`. A shorter viewport or longer
    // content both RAISE the maximum offset, so neither disturbs the reader.
    const browserMayHaveLoweredScrollTop = s.clientHeight > s.lastClientHeight || s.scrollHeight < s.lastScrollHeight;
    if (browserMayHaveLoweredScrollTop || s.settling) {
        return { action: 'ignore', leftLiveEdge: s.leftLiveEdge };
    }
    const movedUp = s.scrollTop < s.lastScrollTop - FOLLOW_SCROLL_UP_EPSILON_PX;
    const gap = s.scrollHeight - s.scrollTop - s.clientHeight;
    const leftLiveEdge = gap > FOLLOW_BOTTOM_TOLERANCE_PX ? true : s.leftLiveEdge;
    // Latch on release, not only on distance. Scrolling up IS leaving the live
    // edge, whatever the distance: without this, a nudge smaller than the
    // tolerance released follow and then nothing could ever re-arm it, because
    // `gap` never exceeded the tolerance to set the latch. The reader was left
    // sitting at the live edge with follow stuck off — the opposite of what MU
    // asked for on 17. 9. 2026. (The follow TOGGLE does not route through here:
    // switching follow off disarms the tracker, which clears a latch left over
    // from an earlier catch-up, and the follow loop rebases the tracker after
    // its own writes, so neither can be read as the reader returning and undo
    // a switch-off.)
    if (movedUp)
        return { action: 'release', leftLiveEdge: true };
    // Re-arming requires the reader to have genuinely left the live edge first,
    // AND to be moving down now. Without the direction check, the eased tail of
    // the same upward gesture (frames of 2px or less, inside the deadzone)
    // re-armed follow straight after releasing it, and the loop pulled the
    // reader back to the end: one ArrowUp or a small trackpad scroll could not
    // release follow at all.
    const movedDown = s.scrollTop > s.lastScrollTop + FOLLOW_SCROLL_DOWN_EPSILON_PX;
    if (movedDown && gap <= FOLLOW_BOTTOM_TOLERANCE_PX && leftLiveEdge) {
        return { action: 'rearm', leftLiveEdge: false };
    }
    return { action: 'none', leftLiveEdge };
}
/**
 * The element's scroll offset, clamped to the range the reader can actually
 * rest at.
 *
 * iOS rubber-bands scroll containers: a flick at the live edge carries
 * `scrollTop` past `scrollHeight - clientHeight`, and the bounce back down
 * then looks exactly like the reader scrolling UP. Unclamped, that released
 * live follow while the reader sat at the very end — on precisely the
 * platform and gesture the app is used with. The same applies at the top,
 * where the overscroll is negative.
 */
function restingScrollTop(el) {
    const max = Math.max(0, el.scrollHeight - el.clientHeight);
    return Math.min(Math.max(el.scrollTop, 0), max);
}
export function createFollowTracker(el) {
    let previous = {
        scrollTop: restingScrollTop(el),
        clientHeight: el.clientHeight,
        scrollHeight: el.scrollHeight,
    };
    let leftLiveEdge = false;
    function track(options = {}) {
        const current = {
            scrollTop: restingScrollTop(el),
            clientHeight: el.clientHeight,
            scrollHeight: el.scrollHeight,
        };
        const decision = decideFollowOnScroll({
            scrollTop: current.scrollTop,
            lastScrollTop: previous.scrollTop,
            clientHeight: current.clientHeight,
            lastClientHeight: previous.clientHeight,
            scrollHeight: current.scrollHeight,
            lastScrollHeight: previous.scrollHeight,
            settling: options.settling ?? false,
            leftLiveEdge,
        });
        previous = current;
        leftLiveEdge = decision.leftLiveEdge;
        return decision.action;
    }
    function rebase() {
        previous = { ...previous, scrollTop: restingScrollTop(el) };
    }
    function disarm() {
        leftLiveEdge = false;
    }
    function leave() {
        leftLiveEdge = true;
    }
    return Object.assign(track, { rebase, disarm, leave });
}
/**
 * True for a key that scrolls the transcript towards older text. Live follow
 * must be released on the key press itself, before the browser starts its
 * smooth scroll: the follow loop writes `scrollTop` every frame, and a script
 * write cancels a smooth scroll in progress, so the scroll moved a pixel or
 * two (inside FOLLOW_SCROLL_UP_EPSILON_PX) and follow never let go. PageUp
 * "worked from about the third press" while text was arriving (retest
 * 24. 9. 2026, C).
 */
export function isFollowReleaseKey(k) {
    if (k.inFormField)
        return false;
    if (k.key === 'PageUp' || k.key === 'ArrowUp' || k.key === 'Home')
        return true;
    return k.key === ' ' && k.shiftKey;
}
//# sourceMappingURL=viewer-follow.js.map